import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Flex } from "orcs-design-system";
import { isEqual, isString } from "lodash";

import { isReadOnly } from "src/components/OrgChart/context/utils/getCalculatedState";
import { EventBus } from "src/services/eventEmitter";
import { ORG_CHART_PANEL_EVENTS } from "src/components/OrgChart/components/OrgChartPanel/const";
import { PanelLine } from "src/components/OrgChart/components/OrgChartPanel/panels/shared/PanelLine";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import GroupTypeBadge from "src/components/Badge/GroupTypeBadge";
import TeamAvatar from "src/components/TeamAvatar/TeamAvatar";
import { useUpdateTeamName } from "src/components/OrgChart/components/OrgChartPanel/panels/Details/TeamDetails/hooks/useUpdateTeamName";

import { PanelSection } from "../../shared/PanelSection";
import { useUpdateTeamAttributes } from "./hooks/useUpdateTeamAttributes";
import { Name } from "./sections/Name";
import { Description } from "./sections/Description";
import { Links } from "./sections/Links";
import { Tags } from "./sections/Tags";

export const TeamDetails = ({ selectedTeam }) => {
  const workspace = useWorkspace();

  const [selectedTeamId, setSelectedTeamId] = useState(selectedTeam.id);

  // whether anything is being edited
  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const [isEditingLinks, setIsEditingLinks] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);

  // holds new data for currently editing section
  const [newAttributes, setNewAttributes] = useState(null);
  const [newName, setNewName] = useState(selectedTeam.name);

  // whether we are waiting for a data refresh after saving
  const [isSaving, setIsSaving] = useState(false);

  // how we update team attributes
  const { updateTeamAttributes, canUpdateTeamAttributes } =
    useUpdateTeamAttributes({
      team: selectedTeam,
    });

  // how we update team name
  const { updateTeamName, canUpdateTeamName } = useUpdateTeamName({
    team: selectedTeam,
  });

  const resetState = useCallback(() => {
    setSelectedTeamId(selectedTeam.id);
    setIsEditingDesc(false);
    setIsEditingLinks(false);
    setIsEditingName(false);
    setNewAttributes(null);
    setNewName(selectedTeam.name);
  }, [selectedTeam.id, selectedTeam.name]);

  // reset state on team change
  useEffect(() => {
    if (selectedTeamId !== selectedTeam.id) {
      resetState();
    }
  }, [resetState, selectedTeam.id, selectedTeam.name, selectedTeamId]);

  useEffect(() => {
    const handleEditTeamDetails = () => {
      setIsEditingDesc(true);
    };

    EventBus.on(
      ORG_CHART_PANEL_EVENTS.EDIT_TEAM_DETAILS,
      handleEditTeamDetails
    );

    return () => {
      EventBus.off(
        ORG_CHART_PANEL_EVENTS.EDIT_TEAM_DETAILS,
        handleEditTeamDetails
      );
    };
  }, []);

  // KNOWN ISSUE: if user quickly changes selected team while saving, you can get a
  // "Maximum update depth exceeded." error. This is not a big deal, but fixing it will
  // require adjusting orcs/FloatingPanels to allow for preventing new selections.
  const onSave = useCallback(async () => {
    resetState();
    setIsSaving(true);

    // updating attributes?
    if (isEditingDesc || isEditingLinks) {
      // skip if no change
      if (!isEqual(newAttributes, selectedTeam.attributes)) {
        await updateTeamAttributes(newAttributes);
      }
    }
    // updateing name?
    else if (isEditingName) {
      // skip if no change
      if (!isEqual(newName, selectedTeam.name)) {
        await updateTeamName(newName);
      }
    }

    setIsSaving(false);
  }, [
    resetState,
    isEditingDesc,
    isEditingLinks,
    isEditingName,
    newAttributes,
    selectedTeam.attributes,
    selectedTeam.name,
    updateTeamAttributes,
    newName,
    updateTeamName,
  ]);

  const readOnly = isReadOnly(selectedTeam);

  return (
    <Flex flexDirection="column" gap="r">
      <Flex
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        width="100%"
        gap="s"
      >
        <TeamAvatar
          sizing="large"
          group={selectedTeam}
          imageAlt={`Avatar of ${selectedTeam.name}`}
        />
        <PanelLine
          centered={true}
          canEdit={canUpdateTeamName && !readOnly}
          canSave={isString(newName) && newName.length > 0 && !readOnly}
          isEditing={isEditingName}
          isSaving={isSaving}
          onSave={onSave}
          onEdit={() => {
            resetState();
            setIsEditingName(true);
          }}
          onCancel={resetState}
        >
          <Name
            team={selectedTeam}
            isEditing={isEditingName}
            setNewName={setNewName}
            newName={newName}
          />
        </PanelLine>
        <GroupTypeBadge
          showBreadcrumbIcon={false}
          group={selectedTeam}
          groupTypes={workspace?.config?.groupTypes}
        />
      </Flex>
      <PanelSection
        name="DESCRIPTION"
        canEdit={canUpdateTeamAttributes && !readOnly}
        canSave={!readOnly}
        isEditing={isEditingDesc}
        isSaving={isSaving}
        onSave={onSave}
        onEdit={() => {
          resetState();
          setIsEditingDesc(true);
        }}
        onCancel={resetState}
      />
      <Description
        team={selectedTeam}
        isEditing={isEditingDesc}
        setNewAttributes={setNewAttributes}
      />
      <PanelSection
        name="LINKS"
        canEdit={canUpdateTeamAttributes && !readOnly}
        canSave={!readOnly}
        isEditing={isEditingLinks}
        isSaving={isSaving}
        onSave={onSave}
        onEdit={() => {
          resetState();
          setIsEditingLinks(true);
        }}
        onCancel={resetState}
      />
      <Links
        team={selectedTeam}
        isEditing={isEditingLinks}
        setNewAttributes={setNewAttributes}
      />
      <PanelSection name="TAGS" />
      <Tags team={selectedTeam} readOnly={readOnly} />
    </Flex>
  );
};

TeamDetails.propTypes = {
  selectedTeam: PropTypes.object.isRequired,
};
